<script>
/**
 * @component CentralAlertas
 * @description Tablero org-level "Central de Alertas" del seguimiento. Muestra,
 *              para toda la organización, KPIs clave + alertas priorizadas en
 *              tarjetas compactas + Foco IA. Consume GET /monitoring/central
 *              (una sola request). Auto-refresca cada 60 s.
 * @example <CentralAlertas />
 */
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import monitoringService from "@/services/monitoring";
import Spinner from "@/components/ui/Spinner";
import TrackingNav from "@/components/ui/TrackingNav.vue";

const KIND_ICON = {
  vital: "pulse",
  "vital-missed": "pulse",
  checkin: "message",
  medication: "pill",
  ia: "alert",
};

/**
 * @description Rango numérico de severidad para ordenar (mayor = más grave).
 */
const SEVERITY_RANK = { critica: 3, media: 2, baja: 1 };

/**
 * @description Máximo de líneas de alerta visibles por tarjeta antes de
 *              colapsar el resto en "+N más".
 */
const MAX_VISIBLE_ITEMS = 4;

export default {
  name: "CentralAlertas",

  components: { Spinner, TrackingNav },

  data() {
    return {
      isLoading: true,
      kpis: {},
      alerts: [],
      focoIA: [],
      filter: "all",
      generatedAt: null,
      timer: null,
    };
  },

  computed: {
    adherenceLabel() {
      return this.kpis.adherencia7d == null
        ? "—"
        : `${this.kpis.adherencia7d}%`;
    },
    filteredAlerts() {
      if (this.filter === "critica") {
        return this.alerts.filter((a) => a.severity === "critica");
      }
      if (this.filter === "unassigned") {
        return this.alerts.filter((a) => !a.doctorName);
      }
      return this.alerts;
    },

    /**
     * @description Agrupa las alertas filtradas por paciente → UNA tarjeta por
     *              paciente. Cada grupo toma la peor severidad de sus alertas,
     *              el conteo total, el ícono/hora de la alerta más grave y la
     *              lista completa de alertas (ordenadas severidad↓, luego más
     *              reciente). Las tarjetas se ordenan severidad↓, luego conteo↓,
     *              luego más reciente. Reemplaza el render 1-tarjeta-por-alerta.
     * @returns {Array<Object>}
     */
    groupedAlerts() {
      const byPatient = new Map();
      for (const a of this.filteredAlerts) {
        const key = a.patientId || a.patientName || a.id;
        if (!byPatient.has(key)) {
          byPatient.set(key, {
            patientId: a.patientId,
            patientName: a.patientName,
            items: [],
          });
        }
        byPatient.get(key).items.push(a);
      }

      const groups = [];
      for (const g of byPatient.values()) {
        g.items.sort((x, y) => {
          const r =
            (SEVERITY_RANK[y.severity] || 0) - (SEVERITY_RANK[x.severity] || 0);
          if (r !== 0) return r;
          return new Date(y.at || 0) - new Date(x.at || 0);
        });

        const worst = g.items[0];
        const withDoctor = g.items.find((i) => i.doctorName);
        const latest = g.items.reduce((acc, i) => {
          if (!i.at) return acc;
          if (!acc) return i.at;
          return new Date(i.at) > new Date(acc) ? i.at : acc;
        }, null);
        const topScore = g.items.reduce(
          (m, i) =>
            i.score != null && (m == null || i.score > m) ? i.score : m,
          null
        );

        g.severity = worst.severity;
        g.kind = worst.kind;
        g.count = g.items.length;
        g.doctorName = withDoctor ? withDoctor.doctorName : null;
        g.at = latest;
        g.score = topScore;
        g.visible = g.items.slice(0, MAX_VISIBLE_ITEMS);
        g.extra = Math.max(0, g.count - MAX_VISIBLE_ITEMS);
        groups.push(g);
      }

      groups.sort((x, y) => {
        const r =
          (SEVERITY_RANK[y.severity] || 0) - (SEVERITY_RANK[x.severity] || 0);
        if (r !== 0) return r;
        if (y.count !== x.count) return y.count - x.count;
        return new Date(y.at || 0) - new Date(x.at || 0);
      });
      return groups;
    },
  },

  mounted() {
    this.load();
    this.timer = setInterval(this.load, 60000);
  },

  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },

  methods: {
    async load() {
      try {
        const data = await monitoringService.getCentral();
        this.kpis = data.kpis || {};
        this.alerts = data.alerts || [];
        this.focoIA = data.focoIA || [];
        this.generatedAt = data.generatedAt;
      } catch (e) {
        // Fail-silent: el layout no se rompe si el endpoint falla.
        this.alerts = [];
      } finally {
        this.isLoading = false;
      }
    },
    setFilter(f) {
      this.filter = f;
    },
    iconFor(kind) {
      return KIND_ICON[kind] || "alert";
    },
    severityClass(severity) {
      return `acard--${severity}`;
    },
    severityLabel(severity) {
      return (
        { critica: "Crítica", media: "Media", baja: "Baja" }[severity] ||
        severity
      );
    },
    scoreClass(score) {
      if (score >= 9) return "fscore--crit";
      if (score >= 7) return "fscore--warn";
      return "";
    },
    /**
     * @description Texto de una línea que describe la alerta según su tipo.
     */
    alertText(a) {
      if (a.kind === "ia") {
        return `Riesgo IA — score ${a.score != null ? a.score : "—"}`;
      }
      if (a.overdue) {
        const verbo = a.kind === "medication" ? "no tomada" : "no registrada";
        return `${a.title} — ${verbo}`;
      }
      const val = a.value != null ? ` = ${a.value}${a.unit || ""}` : "";
      return `${a.title}${val} — fuera de rango`;
    },
    relativeTime(date) {
      if (!date) return "";
      return formatDistanceToNow(new Date(date), {
        addSuffix: true,
        locale: es,
      });
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
    },
    verPaciente(a) {
      if (a.patientId) {
        this.$router
          .push({ name: "patient", params: { id: a.patientId } })
          .catch(() => {});
      }
    },
  },
};
</script>

<template lang="pug">
.central
  TrackingNav(active="central")
  .central__top
    .central__brand
      h1.central__title Central de Alertas
      span.central__live
        span.central__pulse
        | En vivo
    .central__filters
      button.chip(:class="{ on: filter === 'all' }" @click="setFilter('all')") Todas
      button.chip(:class="{ on: filter === 'critica' }" @click="setFilter('critica')") Críticas
      button.chip(:class="{ on: filter === 'unassigned' }" @click="setFilter('unassigned')") Sin asignar

  Spinner(v-if="isLoading")

  .central__body(v-else)
    //- KPIs
    .kpis
      .kpi.kpi--crit
        .kpi__lab Críticas abiertas
        .kpi__val {{ kpis.criticas || 0 }}
      .kpi
        .kpi__lab Adherencia 7 días
        .kpi__val {{ adherenceLabel }}
      .kpi
        .kpi__lab Sin contacto +72 h
        .kpi__val {{ kpis.sinContacto72h || 0 }}
      .kpi
        .kpi__lab En seguimiento
        .kpi__val {{ kpis.enSeguimiento || 0 }}
        .kpi__sub {{ kpis.alertasActivas || 0 }} alertas activas

    .sec-h
      h2 Alertas prioritarias

    .empty(v-if="!groupedAlerts.length")
      micon(name="check_circle_outline")
      span Sin alertas {{ filter === 'all' ? 'activas' : 'para este filtro' }}.

    .cards(v-else)
      .acard(v-for="g in groupedAlerts" :key="g.patientId || g.patientName" :class="severityClass(g.severity)")
        .acard__head
          .acard__ic
            micon(:name="iconFor(g.kind)")
          .acard__id
            .acard__name {{ g.patientName }}
            .acard__sub {{ g.doctorName || 'Sin asignar' }}
          span.acard__n(v-if="g.count > 1") {{ g.count }}
          span.tag {{ severityLabel(g.severity) }}
        ul.acard__list
          li.acard__item(v-for="a in g.visible" :key="a.id")
            span.acard__dot(:class="'dot--' + a.severity")
            span.acard__itext {{ alertText(a) }}
          li.acard__more(v-if="g.extra") +{{ g.extra }} más
        .acard__foot
          span.acard__time {{ relativeTime(g.at) }}
          el-button.acard__btn(size="mini" @click="verPaciente(g)") Ver

    template(v-if="focoIA.length")
      .sec-h
        h2 Foco IA · en riesgo
      .frow
        .fcard(v-for="f in focoIA" :key="f.patientId")
          span.avatar {{ initials(f.patientName) }}
          .fcard__id
            .fcard__name {{ f.patientName }}
          span.fscore(:class="scoreClass(f.score)") {{ f.score }}
</template>

<style scoped lang="scss">
$crit: #cf3d33;
$crit-bg: #fbe8e5;
$crit-line: #f3cbc4;
$warn: #b07d1f;
$warn-bg: #f8f0d9;
$warn-line: #ecd9a8;
$info: #2f6ea3;
$info-bg: #e8f1f9;
$info-line: #c3ddf0;
$ok: #1f8f63;
$ok-bg: #e6f4ed;
$ok-line: #bfe2d0;
$ink: #16203a;
$muted: #5a6785;
$faint: #8a95ad;
$line: #e5e9f0;
$surface: #ffffff;
$surface-2: #eef1f6;
$accent: #1e3270;
$accent-soft: #e9edf7;

.central {
  padding: 4px 2px 24px;
  font-variant-numeric: tabular-nums;
}
.central__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.central__brand {
  display: flex;
  align-items: center;
  gap: 11px;
}
.central__title {
  margin: 0;
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: $ink;
}
.central__live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: $ok;
  background: $ok-bg;
  border: 1px solid $ok-line;
  padding: 3px 10px;
  border-radius: 999px;
}
.central__pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $ok;
  animation: cpulse 2s infinite;
}
@keyframes cpulse {
  0% {
    box-shadow: 0 0 0 0 rgba($ok, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba($ok, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba($ok, 0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .central__pulse {
    animation: none;
  }
}
.central__filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.chip {
  font-size: 12px;
  font-weight: 600;
  color: $muted;
  background: $surface;
  border: 1px solid $line;
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;
  font-family: inherit;
  &.on {
    color: #fff;
    background: $accent;
    border-color: $accent;
  }
}

.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}
.kpi {
  background: $surface;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 12px 14px;
  &--crit {
    background: $crit-bg;
    border-color: $crit-line;
    .kpi__lab,
    .kpi__val {
      color: $crit;
    }
  }
}
.kpi__lab {
  font-size: 11.5px;
  font-weight: 600;
  color: $muted;
}
.kpi__val {
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin-top: 4px;
  color: $ink;
}
.kpi__sub {
  font-size: 11px;
  color: $faint;
  margin-top: 5px;
}

.sec-h {
  margin: 0 2px 11px;
}
.sec-h h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: $ink;
}

.empty {
  display: flex;
  align-items: center;
  gap: 9px;
  color: $muted;
  font-size: 14px;
  background: $surface;
  border: 1px dashed $line;
  border-radius: 12px;
  padding: 22px;
  ::v-deep .micon {
    width: 20px;
    height: 20px;
    color: $ok;
  }
}

.cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.acard {
  background: $surface;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 12px 12px 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  border-left: 6px solid $line;
  &--critica {
    border-left-color: $crit;
  }
  &--media {
    border-left-color: $warn;
  }
  &--baja {
    border-left-color: $info;
  }
}
.acard__head {
  display: flex;
  align-items: center;
  gap: 9px;
}
.acard__ic {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: $surface-2;
  color: $muted;
  ::v-deep .micon {
    width: 17px;
    height: 17px;
  }
}
.acard--critica .acard__ic {
  background: $crit-bg;
  color: $crit;
}
.acard--media .acard__ic {
  background: $warn-bg;
  color: $warn;
}
.acard--baja .acard__ic {
  background: $info-bg;
  color: $info;
}
.acard__id {
  min-width: 0;
  flex: 1;
}
.acard__name {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: $ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.acard__sub {
  font-size: 11px;
  color: $faint;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tag {
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid;
  flex-shrink: 0;
}
.acard--critica .tag {
  color: $crit;
  background: $crit-bg;
  border-color: $crit-line;
}
.acard--media .tag {
  color: $warn;
  background: $warn-bg;
  border-color: $warn-line;
}
.acard--baja .tag {
  color: $info;
  background: $info-bg;
  border-color: $info-line;
}
.acard__n {
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  color: $muted;
  background: $surface-2;
  border-radius: 999px;
  padding: 1px 8px;
  line-height: 1.5;
}
.acard__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.acard__item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  line-height: 1.35;
  color: $ink;
}
.acard__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: $faint;
  &.dot--critica {
    background: $crit;
  }
  &.dot--media {
    background: $warn;
  }
  &.dot--baja {
    background: $info;
  }
}
.acard__itext {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acard__more {
  font-size: 11px;
  font-weight: 600;
  color: $accent;
  padding-left: 13px;
}
.acard__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.acard__time {
  font-size: 11px;
  color: $faint;
}

.frow {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.fcard {
  background: $surface;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 11px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $accent-soft;
  color: $accent;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fcard__id {
  min-width: 0;
  flex: 1;
}
.fcard__name {
  font-weight: 700;
  font-size: 13px;
  color: $ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fscore {
  margin-left: auto;
  font-weight: 800;
  font-size: 16px;
  color: $muted;
  &--crit {
    color: $crit;
  }
  &--warn {
    color: $warn;
  }
}

@media (max-width: 900px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }
  .cards {
    grid-template-columns: repeat(2, 1fr);
  }
  .frow {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 560px) {
  .cards,
  .frow {
    grid-template-columns: 1fr;
  }
}
</style>
